<template>
  <div class="bui-entity-listing-page campaign-editor-redesign">
    <content-with-loading-skeleton :isLoading="isLoading" loadingMessage="Loading Campaign Data..." :isWithMessage="true">
      <template #loaded>
        <div class="campaign-editor-redesign__inner" v-if="!isLoading">
          <!-- Page header -->
          <header class="pe-header">
            <div class="pe-header__left">
              <div class="pe-header__title-row">
                <h1 class="bui-page-title">Campaign Editor</h1>
                <button
                  type="button"
                  class="pe-header__refresh"
                  aria-label="Refresh campaign data"
                  @click="loadCampaign"
                >
                  <PhArrowsClockwise :size="22" weight="bold" />
                </button>
              </div>
              <div class="pe-header__subtitle">{{ basicInfo.name }}</div>
            </div>
            <div class="pe-header__right">
              <CreateAnotherCampaignButton />
            </div>
          </header>

          <!-- Campaign Details -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Campaign Details</h2>
            </template>

            <StatusBar :errorMessage="errorMessage" />

            <div class="ce-details-grid">
              <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
              <div class="d-flex field-row">
                <label for="ce-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
                <TextField id="ce-name" v-model="basicInfo.name" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="ce-start" class="basis-1/2 text-sm font-bold">Start Date</label>
                <DateField id="ce-start" v-model="basicInfo.startDate" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="ce-end" class="basis-1/2 text-sm font-bold">End Date</label>
                <DateField id="ce-end" v-model="basicInfo.endDate" class="basis-1/2" width="100%" :disabled="isReadOnly" />
              </div>
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Active?</label>
                <div class="basis-1/2">
                  <label class="ce-flag">
                    <input type="checkbox" v-model="basicInfo.isEnabled" :disabled="isReadOnly" />
                    Enabled
                  </label>
                </div>
              </div>
            </div>
          </InfoCard>

          <!-- Playlists -->
          <InfoCard flush>
            <template #title>
              <h2 class="info-card__title-text">Playlists</h2>
            </template>
            <template #actions>
              <AddPlaylistDialog
                v-if="!isReadOnly"
                :showBtn="true"
                :campaignTitle="basicInfo.name"
                @save="addPlaylist($event)"
              />
            </template>

            <div class="ce-empty" v-if="playlists.length == 0">
              Campaigns need one or more playlists to run.
            </div>
            <div class="pe-table-wrap" v-if="playlists.length > 0">
              <table class="pe-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Recurrence</th>
                    <th>Time Period</th>
                    <th>Week Days</th>
                    <th>Start-End Date</th>
                    <th>Size</th>
                    <th class="pe-table__actions-col">Order</th>
                    <th class="pe-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <draggable :list="playlists" group="campaignPlaylists" tag="tbody" item-key="itemKey">
                  <template #item="{ element, index }">
                    <tr>
                      <td>{{ index + 1 }}</td>
                      <td>
                        <RouterLink class="campaign-link" :to="{ name: 'playlist-editor', params: { id: element.playlist?.id } }">
                          {{ element.playlist?.name }}
                        </RouterLink>
                      </td>
                      <td>
                        <EditRecurrenceDialog v-if="!isReadOnly" :playlist="element"
                          @save="saveRecurrance($event, index)">
                        </EditRecurrenceDialog>
                        <span v-else>{{ isPlaylistHasCustomRecurrence(element) ? 'Custom' : 'Default' }}</span>
                      </td>
                      <td>{{ `${format24TimeToAMPM(element.startTime)} -
                        ${format24TimeToAMPM(element.endTime)}` }}</td>
                      <td>
                        <p class="weekDayCell">{{ getPlayListRecurrence(element.weekdays) }}</p>
                      </td>
                      <td>
                        <ul class="startDateEndDateCell">
                          <li v-if="element.startDate"><strong>StartDate: </strong>{{ (new
                            Date(element.startDate)).toLocaleDateString() }}</li>
                          <li v-if="element.endDate"><strong>EndDate: </strong>{{ (new
                            Date(element.endDate)).toLocaleDateString()}}</li>
                        </ul>
                      </td>
                      <td>{{ formatBytes(element.playlist?.size) }}</td>
                      <td class="pe-table__actions">
                        <div class="ce-row-actions" v-if="!isReadOnly">
                          <button type="button" class="pe-row-action-btn" aria-label="Move down"
                            @click="reorderPlaylist(index, index + 1)">
                            <PhCaretDown :size="16" weight="bold" />
                          </button>
                          <button type="button" class="pe-row-action-btn" aria-label="Move up"
                            @click="reorderPlaylist(index, index - 1)">
                            <PhCaretUp :size="16" weight="bold" />
                          </button>
                        </div>
                      </td>
                      <td class="pe-table__actions">
                        <div class="ce-row-actions">
                          <div class="view-Icon pe-row-action-btn" aria-label="Preview playlist items">
                            <PhEye :size="16" weight="bold" />
                            <div class="playlist-asset">
                              <img v-for="(item, index) of element.playlist?.items" :key="index" width="140" height="120"
                                :src="item.asset.previewUri" />
                            </div>
                          </div>
                          <button v-if="!isReadOnly" type="button" class="pe-row-action-btn" aria-label="Edit playlist"
                            @click="editPlaylist(element.playlist?.id)">
                            <PhPencilSimple :size="16" weight="bold" />
                          </button>
                          <button v-if="!isReadOnly" type="button" class="pe-row-action-btn" aria-label="Remove playlist"
                            @click="removePlaylist(index)">
                            <PhTrash :size="16" weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </template>
                </draggable>
              </table>
            </div>
            <div class="pe-table-footer" v-if="playlists.length > 0">
              Showing {{ playlists.length }} of {{ playlists.length }} playlist{{ playlists.length === 1 ? '' : 's' }}
            </div>
          </InfoCard>

          <!-- Players -->
          <InfoCard flush>
            <template #title>
              <h2 class="info-card__title-text">Players</h2>
            </template>
            <template #actions>
              <AddPlayertDialog
                v-if="!isReadOnly"
                :showBtn="true"
                :campaignTitle="basicInfo.name"
                @save="addPlayer($event)"
              />
            </template>

            <div class="ce-empty" v-if="players.length == 0">
              Campaigns need to be assigned to one or more physical media players.
            </div>
            <div class="pe-table-wrap" v-if="players.length > 0">
              <table class="pe-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th class="pe-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(player, itemIndex) of players"
                    :key="player.id"
                  >
                    <td>
                      <a class="campaign-link" v-on:click="editPlayer(player.id)">
                        {{ player.name }}
                      </a>
                    </td>
                    <td>
                      <a class="campaign-link" v-on:click="editLocation(player.location?.id)">
                        {{ player.location?.name }}
                      </a>
                    </td>
                    <td class="pe-table__actions">
                      <div class="ce-row-actions" v-if="!isReadOnly">
                        <button type="button" class="pe-row-action-btn" aria-label="Edit player"
                          @click="editPlayer(player.id)">
                          <PhPencilSimple :size="16" weight="bold" />
                        </button>
                        <button type="button" class="pe-row-action-btn" aria-label="Remove player"
                          @click="removePlayer(itemIndex)">
                          <PhTrash :size="16" weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="pe-table-footer" v-if="players.length > 0">
              Showing {{ players.length }} of {{ players.length }} player{{ players.length === 1 ? '' : 's' }}
            </div>
          </InfoCard>

          <!-- Sticky footer -->
          <footer class="editor-footer">
            <div class="editor-footer__inner">
              <ExitButton v-bind="{ hasUnsavedChanges: isReadOnly ? false : hasUnsavedChanges, unsetHasUnsavedChanges }"
                @exit="onExit" @saveAndExit="onSaveAndExit" />
              <SaveCampaignButton v-if="!isReadOnly" ref="saveCampaignButtonRef" />
            </div>
          </footer>
        </div>
      </template>
    </content-with-loading-skeleton>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, computed } from 'vue'
import { PhArrowsClockwise, PhCaretDown, PhCaretUp, PhEye, PhPencilSimple, PhTrash } from '@phosphor-icons/vue';
import DateField from "@/components/form-fields/DateField.vue";
import TextField from "@/components/form-fields/TextField.vue";
import useUser from '@/composables/user';
import useSaveCampaignFormHelpers from "./composables/saveCampaignFormHelpers";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import AddPlaylistDialog from './AddPlaylist/AddPlaylistDialog.vue';
import AddPlayertDialog from './AddPlayer/AddPlayerDialog.vue'
import { formatBytes, format24TimeToAMPM } from '@/helpers/utils';
import EditRecurrenceDialog from './EditRecurrence/EditRecurrenceDialog.vue'
import useCampaign from './composables/campaign';
import SaveCampaignButton from './SaveCampaignButton/SaveCampaignButton.vue';
import ExitButton from '@/components/Dialog/ButtonsWithDialog/ExitButton/ExitButton.vue';
import CreateAnotherCampaignButton from './CreateAnotherCampaignButton/CreateAnotherCampaignButton.vue'
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import sharedRefs from './sharedRefs';
import router from '@/router';
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import draggable from "vuedraggable";

const route = useRoute()
const { saveCampaignButtonRef } = sharedRefs;
const user = useUser();
const isReadOnly = computed(() => !user.hasWriteAccessToArea('Campaigns'));

const {
  basicInfo,
  playlists,
  players,
  loadCampaign,
  isLoading,
  reorderPlaylist,
  removePlayer,
  removePlaylist,
  addPlaylist,
  addPlayer,
  getPlayListRecurrence,
  hasUnsavedChanges,
  unsetHasUnsavedChanges,
  initializeHasUnsavedChangesWatcher,
} = useCampaign();

const { errorMessage } = useSaveCampaignFormHelpers();

initializeHasUnsavedChangesWatcher();

onBeforeMount(() => {
  basicInfo.id = route.params.id as string;
  loadCampaign()
})

onBeforeRouteUpdate(async (newRoute) => {
  if(newRoute.name === "campaign-editor") {
    basicInfo.id = newRoute.params.id as string;
    await loadCampaign();
  }
})

function onExit() {
  router.push({ path: '/campaigns' })
}

async function onSaveAndExit() {
  await saveCampaignButtonRef.value.savePlaylist();
  router.push({ path: '/campaigns' })
}

function editPlayer(id: number) {
  router.push({ name: 'player-editor', params: { id: id } });

}
function editPlaylist(id: number) {
  router.push({ name: 'playlist-editor', params: { id: id } });

}
function editLocation(id: number) {
  router.push({ name: 'location-editor', params: { id: id } });

}
function saveRecurrance(item: any, index: number) {
  playlists[index].startTime = item.startTime
  playlists[index].weekdays = item.weekdays
  playlists[index].endTime = item.endTime
  playlists[index].startDate = item.startDate
  playlists[index].endDate = item.endDate
}

function isPlaylistHasCustomRecurrence(playlist: any) {
  const isAllday = playlist.startTime === "00:00:00" && playlist.endTime === "23:59:59";
  const isEveryday = playlist.weekdays === 127;
  const isDateSameAsCampaign = playlist.startDate == null && playlist.endDate == null;
  return !(isAllday && isEveryday && isDateSameAsCampaign);
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.campaign-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.campaign-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* "Create Another Campaign" as a tertiary pill (Player Editor header treatment). */
.pe-header__right {
  :deep(.button) {
    background-color: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
    color: var(--bui-ter-text, #ffd9e6);
    border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
    border-radius: var(--bui-ter-radius, 8px);
    text-transform: none;
    font-weight: 500;
    height: var(--bui-cta-h, 52px);
    box-shadow: none;
    padding: 0 var(--bui-ter-pad, 14px);

    &:hover {
      background-color: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
    }
  }

  :deep(> .flex) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
  }
}

/* Details laid out two per row on wide screens. */
.ce-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 2rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  :deep(.field label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(.field input[type='text']),
  :deep(.field input[type='date']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    width: 100%;
  }
}

.ce-flag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--bui-tbl-row-text, #2a2a30);
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--bui-chk-checked-fill, #de3da9);
    cursor: pointer;
  }
}

/* Card action buttons (Add Playlist / Add Player) keep the default
   ButtonCustomized look — its --bui-btn-* tokens are already themed. The
   Dialog expander wrapper just needs to hug its button. */
:deep(.info-card__actions .flex) {
  display: inline-flex;
  width: auto;
  flex: 0 0 auto;
}

.ce-empty {
  padding: 0 1.25rem 1rem;
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.875rem;

  /* Inline "add" dialog expander links inside the empty state. */
  :deep(a) {
    color: var(--bui-tbl-row-link, #4b1886);
    font-weight: 600;
    cursor: pointer;
  }
}

.ce-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* Recurrence dialog expander link inside the table. */
.pe-table :deep(a) {
  color: var(--bui-tbl-row-link, #4b1886);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.weekDayCell {
  min-width: 185px;
  margin: 0;
}

.startDateEndDateCell {
  min-width: 130px;
  list-style-type: none;
  margin: 0;
  padding: 0;
}

/* Hover preview of playlist items (kept from the legacy editor). */
.playlist-asset {
  display: none;
  position: absolute;
  right: 0px;
  max-width: 400px;
  overflow-x: auto;
  z-index: 4;
}

.view-Icon {
  position: relative;
}

.view-Icon:hover .playlist-asset {
  display: flex
}
</style>
